import { pdfjs } from 'react-pdf';
import { logger } from '../utils/logger';
import { auth } from '../config/firebase';
import { extractIso286Designation } from './iso286Service';
import {
    parseIso22081ToleranceBandsFromTitleBlockText,
    textReferencesIso22081
} from './iso22081Service';

/**
 * Detected text item with position coordinates (absolute, matching stage dimensions)
 */
export type DrawingTextType = 'dimension' | 'text';

export interface DetectedTextItem {
    text: string;
    x: number;
    y: number;
    width: number;
    height: number;
    source: 'pdfjs' | 'gemini';
    /** Rotation in degrees (0 = horizontal). PDF.js only; Gemini items lack rotation. */
    rotation?: number;
    /** Gemini-classified text type: dimension (numeric size / tolerance) vs text (thread, roughness, note, etc.) */
    textType?: DrawingTextType;
}

/**
 * Result of OCR extraction
 */
export interface OcrExtractionResult {
    success: boolean;
    items: DetectedTextItem[];
    source: 'pdfjs' | 'gemini' | 'none';
    error?: string;
}

const PDFJS_DOC_OPTS = {
    standardFontDataUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@4.8.69/standard_fonts/',
    useSystemFonts: true
};

/** Cap longer edge before sending to vision API (smaller payload + faster inference) */
const OCR_IMAGE_MAX_EDGE_PX = 1280;
const OCR_JPEG_QUALITY = 0.88;

/**
 * Crop from source canvas, downscale if needed, JPEG-encode for upload speed.
 */
function encodeCanvasRegionForVisionApi(
    source: HTMLCanvasElement,
    sx: number,
    sy: number,
    sw: number,
    sh: number
): { base64: string; mimeType: string } {
    const maxEdge = Math.max(sw, sh);
    const ratio = maxEdge > OCR_IMAGE_MAX_EDGE_PX ? OCR_IMAGE_MAX_EDGE_PX / maxEdge : 1;
    const dw = Math.max(1, Math.round(sw * ratio));
    const dh = Math.max(1, Math.round(sh * ratio));
    const out = document.createElement('canvas');
    out.width = dw;
    out.height = dh;
    const ctx = out.getContext('2d');
    if (!ctx) {
        return { base64: '', mimeType: 'image/jpeg' };
    }
    ctx.drawImage(source, sx, sy, sw, sh, 0, 0, dw, dh);
    const dataUrl = out.toDataURL('image/jpeg', OCR_JPEG_QUALITY);
    const base64 = dataUrl.split(',')[1] || '';
    return { base64, mimeType: 'image/jpeg' };
}

async function loadPdfDocument(arrayBuffer: ArrayBuffer) {
    return pdfjs.getDocument({
        data: arrayBuffer,
        ...PDFJS_DOC_OPTS
    }).promise;
}

/**
 * Sort PDF.js text items in visual reading order (top-to-bottom, left-to-right).
 * Content stream order often differs, which breaks joining e.g. "h7" before "Ø6.5".
 */
export function sortDetectedItemsReadingOrder(items: DetectedTextItem[]): DetectedTextItem[] {
    if (items.length <= 1) return [...items];
    const copy = items.map(it => ({ ...it }));
    const hs = copy.map(i => Math.max(i.height ?? 16, 8)).sort((a, b) => a - b);
    const medianH = hs[Math.floor(hs.length / 2)] ?? 16;
    const rowScale = Math.max(medianH * 0.65, 10);
    copy.sort((a, b) => {
        const ra = Math.round(a.y / rowScale);
        const rb = Math.round(b.y / rowScale);
        if (ra !== rb) return ra - rb;
        return a.x - b.x;
    });
    return copy;
}

// Diameter symbols: Ø ø ⌀ ∅, o/O and φ/ϕ/Φ (OCR / font substitution)
const DIAMETER_PREFIX = '[±+\\-Øø⌀∅oOφϕΦ]';
const MEASUREMENT_REGEX = new RegExp(`^${DIAMETER_PREFIX}?\\s*\\d+([.,]\\d+)?\\s*(mm|cm|m|in|inch|inches|°|deg|µm|μm)?\\.?$`, 'i');
const NUMBER_REGEX = new RegExp(`^${DIAMETER_PREFIX}?\\s*\\d+([.,]\\d+)?$`);

/**
 * Check if a text string looks like a measurement/dimension
 */
function isMeasurementText(text: string): boolean {
    const trimmed = text.trim();
    if (!trimmed) return false;

    // Diameter: Ø25, ø10, ⌀5, o10 (OCR fallback)
    if (/^[Øø⌀∅oOφϕΦ]\s*\d+([.,]\d+)?$/.test(trimmed)) return true;

    // Check for measurement with units
    if (MEASUREMENT_REGEX.test(trimmed)) return true;

    // Check for plain numbers (likely dimensions)
    if (NUMBER_REGEX.test(trimmed)) {
        const num = parseFloat(trimmed.replace(/[Øø⌀∅oOφϕΦ\s]/g, '').replace(',', '.'));
        return !isNaN(num) && Math.abs(num) >= 0.001;
    }

    // Check for tolerance notation like "0.05" or "+0.02/-0.01"
    if (/^[±+\-]?\d+([.,]\d+)?\s*\/\s*[±+\-]?\d+([.,]\d+)?$/.test(trimmed)) return true;

    return false;
}

/**
 * Extract text items from PDF using PDF.js built-in text layer.
 * This works for PDFs with embedded text (not scanned images).
 *
 * @param file - PDF File object
 * @param pageNumber - Page number to extract from (1-based)
 * @param stageWidth - Current stage width for coordinate mapping
 * @param stageHeight - Current stage height for coordinate mapping
 */
export async function extractTextFromPdfJs(
    file: File,
    pageNumber: number,
    stageWidth: number,
    stageHeight: number
): Promise<OcrExtractionResult> {
    try {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await loadPdfDocument(arrayBuffer);
        const page = await pdf.getPage(pageNumber);
        const textContent = await page.getTextContent();
        const viewport = page.getViewport({ scale: 1 });

        const rawCount = textContent.items.length;
        const withStr = textContent.items.filter((it: any) => 'str' in it && it.str && String(it.str).trim()).length;
        if (rawCount === 0) {
            logger.warn('[OCR] PDF.js getTextContent returned 0 items - PDF may be scanned/image-only');
        } else if (withStr === 0) {
            logger.warn('[OCR] PDF.js items have no str - font encoding issue');
        } else {
            logger.log(`[OCR] PDF.js raw items: ${rawCount}, with text: ${withStr}`);
        }

        // Match react-pdf Page: uniform scale to fit, content centered (letterboxing)
        const scale = Math.min(stageWidth / viewport.width, stageHeight / viewport.height);
        const offsetX = (stageWidth - viewport.width * scale) / 2;
        const offsetY = (stageHeight - viewport.height * scale) / 2;

        const items: DetectedTextItem[] = [];

        for (const item of textContent.items) {
            // Skip non-text items
            if (!('str' in item) || !item.str) continue;
            const text = String(item.str).trim();
            if (!text) continue;

            const textItem = item as any;

            // Include measurements (digits) or a lone diameter glyph (PDF often splits Ø from the number)
            const singleDiamGlyph = text.length === 1 && /[Øø⌀∅oOφϕΦ]/.test(text);
            if (!/\d/.test(text) && !singleDiamGlyph) continue;

            // PDF.js text items have transform [scaleX, skewY, skewX, scaleY, translateX, translateY]
            const tx = textItem.transform[4];
            const ty = textItem.transform[5];
            const itemWidth = textItem.width || 0;
            const itemHeight = textItem.height || Math.abs(textItem.transform[3]);

            // Convert from PDF (origin bottom-left) to stage (origin top-left), with uniform scale + offset
            const x = tx * scale + offsetX;
            const y = (viewport.height - ty - itemHeight) * scale + offsetY;
            const width = itemWidth * scale;
            const height = itemHeight * scale;

            // Extract rotation from transform [a, b, c, d, e, f]: angle = atan2(b, a)
            // PDF Y is up, canvas Y is down; negate for canvas rotation
            const rotRad = Math.atan2(textItem.transform[1], textItem.transform[0]);
            const rotation = -rotRad * (180 / Math.PI);

            // Skip items with zero dimensions
            if (width < 1 || height < 1) continue;

            // Vertical text: use height for horizontal center (not width) so marker doesn't shift with number length
            const isVertical = Math.abs(rotation) > 45 && Math.abs(rotation) < 135;
            const centerX = x + width / 2;
            const isLeftSide = centerX < stageWidth / 2;
            const xAdj = isVertical ? (isLeftSide ? -20 : -20) : 0;
            const xPos = isVertical ? x + height / 2 - width / 2 + xAdj : x + xAdj;

            items.push({
                text,
                x: xPos,
                y,
                width: Math.max(width, 30), // Minimum width for visibility
                height: Math.max(height, 16), // Minimum height for visibility
                source: 'pdfjs',
                rotation: Math.abs(rotation) > 1 ? rotation : 0
            });
        }

        const sortedItems = sortDetectedItemsReadingOrder(items);

        logger.log(`[OCR] PDF.js extracted ${sortedItems.length} measurement items from page ${pageNumber}`);
        if (sortedItems.length > 0) {
            const debugTexts = sortedItems.map((it, i) => {
                const chars = [...it.text].map(c => `${c}(U+${c.charCodeAt(0).toString(16).toUpperCase().padStart(4, '0')})`).join(' ');
                return `[${i}] "${it.text}" [${chars}]`;
            });
            logger.log('[OCR] PDF.js raw items:', debugTexts.join(' | '));
        }

        if (sortedItems.length > 0) {
            return {
                success: true,
                items: sortedItems,
                source: 'pdfjs'
            };
        }

        logger.log('[OCR] PDF.js: no measurement items on page (Gemini may be used as fallback)');
        return {
            success: true,
            items: [],
            source: 'none'
        };
    } catch (error: any) {
        logger.error('[OCR] PDF.js text extraction failed:', error);
        return {
            success: false,
            items: [],
            source: 'none',
            error: 'PDF text extraction failed'
        };
    }
}

/**
 * Resolve the PDF render canvas inside the viewer (not Konva overlay).
 */
export function getPdfPageCanvasFromViewerContainer(
    container: HTMLElement | null
): HTMLCanvasElement | null {
    if (!container) return null;
    const fromWrapper = container.querySelector('.pdf-canvas-wrapper canvas');
    if (fromWrapper instanceof HTMLCanvasElement) return fromWrapper;
    const fromPageClass = container.querySelector('.react-pdf__Page__canvas');
    if (fromPageClass instanceof HTMLCanvasElement) return fromPageClass;
    const first = container.querySelector('canvas');
    return first instanceof HTMLCanvasElement ? first : null;
}

/**
 * Region bounds for filtering text extraction
 */
export interface RegionBounds {
    x: number;
    y: number;
    width: number;
    height: number;
}

/** Konva/Excel rects may use negative width or height; region overlap and canvas crop need positive box. */
function normalizeRegionBounds(region: RegionBounds): RegionBounds {
    const w = region.width;
    const h = region.height;
    return {
        x: w < 0 ? region.x + w : region.x,
        y: h < 0 ? region.y + h : region.y,
        width: Math.abs(w),
        height: Math.abs(h)
    };
}

/**
 * Check if a text item overlaps with a given region
 */
function isItemInRegion(
    item: { x: number; y: number; width: number; height: number },
    region: RegionBounds
): boolean {
    const itemRight = item.x + item.width;
    const itemBottom = item.y + item.height;
    const regionRight = region.x + region.width;
    const regionBottom = region.y + region.height;

    // Check if rectangles overlap
    return (
        item.x < regionRight &&
        itemRight > region.x &&
        item.y < regionBottom &&
        itemBottom > region.y
    );
}

/**
 * Extract text items from PDF within a specific rectangle region.
 * Uses PDF.js built-in text layer, filtered to items inside the region.
 *
 * @param file - PDF File object
 * @param pageNumber - Page number (1-based)
 * @param stageWidth - Stage width for coordinate mapping
 * @param stageHeight - Stage height for coordinate mapping
 * @param region - Rectangle region bounds (stage coordinates)
 */
export async function extractTextFromPdfJsInRegion(
    file: File,
    pageNumber: number,
    stageWidth: number,
    stageHeight: number,
    regionArg: RegionBounds
): Promise<OcrExtractionResult> {
    try {
        const region = normalizeRegionBounds(regionArg);
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await loadPdfDocument(arrayBuffer);
        const page = await pdf.getPage(pageNumber);
        const textContent = await page.getTextContent();
        const viewport = page.getViewport({ scale: 1 });

        // Match react-pdf Page: uniform scale to fit, content centered (letterboxing)
        const scale = Math.min(stageWidth / viewport.width, stageHeight / viewport.height);
        const offsetX = (stageWidth - viewport.width * scale) / 2;
        const offsetY = (stageHeight - viewport.height * scale) / 2;

        const items: DetectedTextItem[] = [];
        const allInRegion: Array<{ text: string; charCodes: string }> = [];

        for (const item of textContent.items) {
            if (!('str' in item) || !item.str) continue;
            const text = String(item.str).trim();
            if (!text) continue;

            const textItem = item as any;

            // For region extraction, accept all non-empty text (not only measurements)
            const tx = textItem.transform[4];
            const ty = textItem.transform[5];
            const itemWidth = textItem.width || 0;
            const itemHeight = textItem.height || Math.abs(textItem.transform[3]);

            const x = tx * scale + offsetX;
            const y = (viewport.height - ty - itemHeight) * scale + offsetY;
            const width = itemWidth * scale;
            const height = itemHeight * scale;

            const rotRad = Math.atan2(textItem.transform[1], textItem.transform[0]);
            const rotation = -rotRad * (180 / Math.PI);

            if (width < 1 || height < 1) continue;

            const isVertical = Math.abs(rotation) > 45 && Math.abs(rotation) < 135;
            const centerX = x + width / 2;
            const isLeftSide = centerX < stageWidth / 2;
            const xAdj = isVertical ? (isLeftSide ? -40 : -20) : 0;
            const xPos = isVertical ? x + height / 2 - width / 2 + xAdj : x + xAdj;

            const mappedItem = {
                x: xPos,
                y,
                width: Math.max(width, 30),
                height: Math.max(height, 16),
                rotation: Math.abs(rotation) > 1 ? rotation : 0
            };

            // Only include items that overlap with the region
            if (!isItemInRegion(mappedItem, region)) continue;

            const charCodes = [...text].map(c => `${c}(U+${c.charCodeAt(0).toString(16).toUpperCase().padStart(4, '0')})`).join(' ');
            allInRegion.push({ text, charCodes });

            // Region OCR: keep any non-empty text that overlaps the box (not only measurements)
            items.push({
                text,
                ...mappedItem,
                source: 'pdfjs'
            });
        }

        const sortedRegionItems = sortDetectedItemsReadingOrder(items);

        if (allInRegion.length > 0) {
            logger.log('[OCR] PDF.js ALL items in region (before digit filter):', allInRegion.map((r, i) => `[${i}] "${r.text}" ${r.charCodes}`).join(' | '));
        }

        logger.log(
            `[OCR] PDF.js region extraction: ${sortedRegionItems.length} items ` +
            `in region (${Math.round(region.x)},${Math.round(region.y)},` +
            `${Math.round(region.width)},${Math.round(region.height)})`
        );
        if (sortedRegionItems.length > 0) {
            const joinedText = sortedRegionItems.map(it => it.text).join(' ');
            const debugItems = sortedRegionItems.map((it, i) => {
                const chars = [...it.text].map(c => `${c}(U+${c.charCodeAt(0).toString(16).toUpperCase().padStart(4, '0')})`).join(' ');
                return `[${i}] "${it.text}" [${chars}]`;
            });
            logger.log('[OCR] PDF.js region items:', debugItems.join(' | '));
            logger.log('[OCR] PDF.js region joined:', `"${joinedText}"`, 'charCodes:', [...joinedText].map(c => c.charCodeAt(0).toString(16).toUpperCase()).join(' '));
        }

        return {
            success: true,
            items: sortedRegionItems,
            source: sortedRegionItems.length > 0 ? 'pdfjs' : 'none'
        };
    } catch (error: any) {
        logger.error('[OCR] PDF.js region extraction failed:', error);
        return {
            success: false,
            items: [],
            source: 'none',
            error: 'PDF region text extraction failed'
        };
    }
}

/**
 * Map stage/Konva rectangle to PDF canvas bitmap pixels.
 * Viewer CSS uses object-fit: contain on the canvas (fixed stage-sized box), so the bitmap
 * is letterboxed; naive region * (canvas/stage) maps to the wrong vertical/horizontal band.
 */
export function mapStageRegionToCanvasBitmapRect(
    canvas: HTMLCanvasElement,
    region: RegionBounds,
    stageWidth: number,
    stageHeight: number
): { cropX: number; cropY: number; cropW: number; cropH: number } {
    const bw = canvas.width;
    const bh = canvas.height;
    if (bw <= 0 || bh <= 0 || stageWidth <= 0 || stageHeight <= 0) {
        return { cropX: 0, cropY: 0, cropW: 1, cropH: 1 };
    }

    const s = Math.min(stageWidth / bw, stageHeight / bh);
    const dispW = bw * s;
    const dispH = bh * s;
    const offX = (stageWidth - dispW) / 2;
    const offY = (stageHeight - dispH) / 2;

    const x0 = (region.x - offX) / s;
    const y0 = (region.y - offY) / s;
    const w0 = region.width / s;
    const h0 = region.height / s;

    let cropX = Math.floor(x0);
    let cropY = Math.floor(y0);
    let cropW = Math.ceil(w0);
    let cropH = Math.ceil(h0);

    cropX = Math.max(0, Math.min(cropX, bw - 1));
    cropY = Math.max(0, Math.min(cropY, bh - 1));
    cropW = Math.max(1, Math.min(cropW, bw - cropX));
    cropH = Math.max(1, Math.min(cropH, bh - cropY));

    return { cropX, cropY, cropW, cropH };
}

/**
 * Strip markdown code fences (```json ... ```) that Gemini often wraps around JSON output.
 */
function stripMarkdownCodeFences(text: string): string {
    const m = text.match(/```(?:json|JSON)?\s*\n?([\s\S]*?)\n?\s*```/);
    return m?.[1]?.trim() || text;
}

/**
 * Parse Gemini region replies: a JSON array, or a comma-separated list of
 * {"text":"...","type":"..."} objects without outer brackets (common model output).
 */
function parseGeminiRegionStructuredResponse(text: string): unknown[] {
    let raw = text.trim();
    if (!raw) {
        return [];
    }

    raw = stripMarkdownCodeFences(raw);

    if (raw.startsWith('[')) {
        try {
            const arr = JSON.parse(raw);
            if (Array.isArray(arr) && arr.length > 0) {
                return arr;
            }
        } catch {
            // fall through
        }
    }

    try {
        const jsonMatch = raw.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
            const arr = JSON.parse(jsonMatch[0]);
            if (Array.isArray(arr) && arr.length > 0) {
                return arr;
            }
        }
    } catch {
        // fall through
    }

    if (raw.includes('"text"') && raw.startsWith('{')) {
        try {
            const arr = JSON.parse(`[${raw}]`);
            if (Array.isArray(arr) && arr.length > 0) {
                return arr;
            }
        } catch {
            return [];
        }
    }

    return [];
}

/**
 * Extract text from a specific canvas region using Gemini Vision API.
 * Crops the canvas to the rectangle region before sending.
 *
 * @param canvas - HTML Canvas element with the rendered PDF page
 * @param stageWidth - Stage width
 * @param stageHeight - Stage height
 * @param region - Rectangle region bounds (stage coordinates)
 */
export async function extractTextFromGeminiInRegion(
    canvas: HTMLCanvasElement,
    stageWidth: number,
    stageHeight: number,
    regionArg: RegionBounds
): Promise<OcrExtractionResult> {
    try {
        const region = normalizeRegionBounds(regionArg);
        const { cropX, cropY, cropW, cropH } = mapStageRegionToCanvasBitmapRect(
            canvas,
            region,
            stageWidth,
            stageHeight
        );

        logger.log(
            `[OCR] Gemini region bitmap crop: (${cropX},${cropY},${cropW},${cropH}) ` +
            `bitmap=${canvas.width}x${canvas.height} stage=${stageWidth}x${stageHeight}`
        );

        const { base64: base64Data, mimeType } = encodeCanvasRegionForVisionApi(
            canvas,
            cropX,
            cropY,
            cropW,
            cropH
        );

        if (!base64Data) {
            return {
                success: false,
                items: [],
                source: 'none',
                error: 'Failed to capture canvas region'
            };
        }

        const headers = await getAuthHeaders();
        const apiUrl = buildApiUrl('ocr/extract');

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                image: base64Data,
                mimeType,
                prompt:
                    'You are analyzing a small cropped region of a technical/engineering drawing. ' +
                    'Transcribe ALL visible text and numbers exactly as printed. ' +
                    'Classify each item as "dimension" (numeric size with or without tolerances, ' +
                    'diameter Ø/R prefix, angle °, chamfer) or "text" (thread M8x1.5, roughness Ra/Rz, ' +
                    'material, hardness HRC/HB, section label A-A, note, or any non-dimensional callout). ' +
                    'Reply as a single JSON array only, e.g. [{"text":"25.4","type":"dimension"},{"text":"M8x1.5","type":"text"}]. ' +
                    'No extra commentary.'
            })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                items: [],
                source: 'none',
                error: errorData.error || `Server error: ${response.status}`
            };
        }

        const result = await response.json();

        if (result && result.success === false) {
            return {
                success: false,
                items: [],
                source: 'none',
                error: typeof result.error === 'string' && result.error
                    ? result.error
                    : 'Recognition failed'
            };
        }

        if (!result.text) {
            return { success: true, items: [], source: 'gemini' };
        }

        const items: DetectedTextItem[] = [];
        let parsedItems: unknown[] = parseGeminiRegionStructuredResponse(result.text);

        if (parsedItems.length === 0 && result.text.trim()) {
            const stripped = stripMarkdownCodeFences(result.text.trim());
            const lines = stripped
                .split(/\n/)
                .map((l: string) => l.trim())
                .filter((l: string) => l.length > 0);
            parsedItems = lines.flatMap((line: string) => {
                if (line.startsWith('{') && line.endsWith('}')) {
                    try {
                        const obj = JSON.parse(line);
                        if (obj && typeof obj.text === 'string') return [obj];
                    } catch { /* fall through */ }
                }
                if (line.startsWith('[') && line.endsWith(']')) {
                    try {
                        const arr = JSON.parse(line);
                        if (Array.isArray(arr)) return arr;
                    } catch { /* fall through */ }
                }
                return [{ text: line }];
            });
        }

        parsedItems.forEach((item: any) => {
            const text = (item.text || String(item)).trim();
            if (!text) return;

            const rawType = typeof item.type === 'string' ? item.type.toLowerCase() : '';
            const textType: DrawingTextType | undefined =
                rawType === 'dimension' ? 'dimension' :
                rawType === 'text' ? 'text' :
                undefined;

            items.push({
                text,
                x: region.x,
                y: region.y,
                width: region.width,
                height: region.height,
                source: 'gemini',
                textType
            });
        });

        logger.log(`[OCR] Gemini region extraction: ${items.length} items`);

        return {
            success: true,
            items,
            source: 'gemini'
        };
    } catch (error: any) {
        logger.error('[OCR] Gemini region extraction failed:', error);
        return {
            success: false,
            items: [],
            source: 'none',
            error: 'Gemini region extraction failed'
        };
    }
}

/**
 * Helper: get auth headers for backend calls
 */
async function getAuthHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
        'Content-Type': 'application/json'
    };
    try {
        const currentUser = auth.currentUser;
        if (currentUser) {
            const idToken = await currentUser.getIdToken();
            headers['Authorization'] = `Bearer ${idToken}`;
        }
    } catch (error) {
        logger.error('[OCR] Error getting auth token:', error);
    }
    return headers;
}

/**
 * Helper: build backend API URL
 */
function buildApiUrl(endpoint: string): string {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002';
    const baseUrl = backendUrl.replace(/\/+$/, '');
    const cleanEndpoint = endpoint.replace(/^\/+/, '');
    return baseUrl.endsWith('/api')
        ? `${baseUrl}/${cleanEndpoint}`
        : `${baseUrl}/api/${cleanEndpoint}`;
}

/**
 * Extract text from a canvas region using Gemini Vision API (backend).
 * Used as fallback when PDF.js doesn't find embedded text.
 *
 * @param canvas - HTML Canvas element with the rendered PDF page
 * @param stageWidth - Stage width
 * @param stageHeight - Stage height
 */
export async function extractTextFromGemini(
    canvas: HTMLCanvasElement,
    stageWidth: number,
    stageHeight: number
): Promise<OcrExtractionResult> {
    try {
        const { base64: base64Data, mimeType } = encodeCanvasRegionForVisionApi(
            canvas,
            0,
            0,
            canvas.width,
            canvas.height
        );

        if (!base64Data) {
            return { success: false, items: [], source: 'none', error: 'Failed to capture canvas' };
        }

        const headers = await getAuthHeaders();
        const apiUrl = buildApiUrl('ocr/extract');

        const imgWidth = canvas.width;
        const imgHeight = canvas.height;

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                image: base64Data,
                mimeType,
                prompt:
                    'You are a precision OCR system analyzing a metric technical/engineering drawing. ' +
                    'Find ALL dimension numbers, measurements, and tolerance values visible in this image. ' +
                    'Read each value EXACTLY as printed — preserve commas vs dots, Ø/ø/⌀ (diameter) vs 0, ± signs. ' +
                    'All dimensions are in metric (mm). ' +
                    'For each found value, return a TIGHT bounding box using coordinates from 0 to 1000 ' +
                    '(where 0,0 is the top-left and 1000,1000 is the bottom-right of the image). ' +
                    'Return ONLY a valid JSON array, no other text:\n' +
                    '[{"text": "25,4", "cx": 452, "cy": 301, "w": 50, "h": 20}]\n' +
                    'Where cx,cy is the CENTER of the text bounding box and w,h is the full width/height. ' +
                    'The bounding box must tightly wrap the text characters with minimal padding. ' +
                    'Include: dimension numbers, numbers with units, tolerances (±0.05), ' +
                    'diameters (Ø25, ø10, ⌀5 — use the exact symbol shown), threads (M10, R1/2), angles, chamfers (2x45°). ' +
                    'Do NOT include title block text, revision info, sheet numbers, scale text, or non-measurement text.'
            })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                items: [],
                source: 'none',
                error: errorData.error || `Server error: ${response.status}`
            };
        }

        const result = await response.json();

        if (result && result.success === false) {
            return {
                success: false,
                items: [],
                source: 'none',
                error: typeof result.error === 'string' && result.error
                    ? result.error
                    : 'Recognition failed'
            };
        }

        if (!result.text) {
            return { success: true, items: [], source: 'gemini' };
        }

        // Parse Gemini response — expect JSON array with coordinates
        const items: DetectedTextItem[] = [];
        let parsedItems: any[] = [];

        try {
            const jsonMatch = result.text.match(/\[[\s\S]*\]/);
            if (jsonMatch) {
                parsedItems = JSON.parse(jsonMatch[0]);
            }
        } catch {
            // Fallback: parse as line-by-line text (no coordinates available)
            const lines = result.text.split('\n').filter((l: string) => l.trim());
            parsedItems = lines.map((line: string) => ({ text: line.trim() }));
        }

        // Map Gemini coords to stage coords accounting for object-fit: contain letterboxing
        const scale = Math.min(stageWidth / imgWidth, stageHeight / imgHeight);
        const renderedW = imgWidth * scale;
        const renderedH = imgHeight * scale;
        const offsetX = (stageWidth - renderedW) / 2;
        const offsetY = (stageHeight - renderedH) / 2;

        logger.log(`[OCR] Gemini canvas=${imgWidth}x${imgHeight}, stage=${stageWidth}x${stageHeight}, rendered=${renderedW.toFixed(0)}x${renderedH.toFixed(0)}, offset=(${offsetX.toFixed(0)},${offsetY.toFixed(0)})`);

        // Detect coordinate scale from data
        const maxCoordAll = parsedItems.reduce((max: number, it: any) => {
            if (Array.isArray(it.box) && it.box.length === 4) {
                return Math.max(max, ...it.box);
            }
            const cx = it.cx ?? it.x ?? 0;
            const cy = it.cy ?? it.y ?? 0;
            return Math.max(max, cx, cy, cx + (it.w || 0), cy + (it.h || 0));
        }, 0);
        const div = maxCoordAll > 100 ? 1000 : 100;
        logger.log(`[OCR] Gemini coord scale: 0-${div} (max seen: ${maxCoordAll.toFixed(0)})`);

        parsedItems.forEach((item: any) => {
            const text = (item.text || String(item)).trim();
            if (!text) return;

            // box=[ymin, xmin, ymax, xmax] format
            if (Array.isArray(item.box) && item.box.length === 4) {
                const [ymin, xmin, ymax, xmax] = item.box;
                const x = (xmin / div) * renderedW + offsetX;
                const y = (ymin / div) * renderedH + offsetY;
                const w = ((xmax - xmin) / div) * renderedW;
                const h = ((ymax - ymin) / div) * renderedH;
                items.push({
                    text,
                    x,
                    y,
                    width: Math.max(w, 8),
                    height: Math.max(h, 8),
                    source: 'gemini'
                });
                return;
            }

            // cx,cy (center) or x,y (top-left) format
            const hasCenterCoords = typeof item.cx === 'number' && typeof item.cy === 'number';
            const hasCoords = hasCenterCoords || (typeof item.x === 'number' && typeof item.y === 'number');
            const wNorm = typeof item.w === 'number' ? item.w / div : 0.04;
            const hNorm = typeof item.h === 'number' ? item.h / div : 0.02;

            let xTopLeft: number;
            let yTopLeft: number;

            if (hasCenterCoords) {
                xTopLeft = (item.cx / div) * renderedW + offsetX - (wNorm * renderedW) / 2;
                yTopLeft = (item.cy / div) * renderedH + offsetY - (hNorm * renderedH) / 2;
            } else if (hasCoords) {
                xTopLeft = (item.x / div) * renderedW + offsetX;
                yTopLeft = (item.y / div) * renderedH + offsetY;
            } else {
                xTopLeft = offsetX;
                yTopLeft = offsetY;
            }

            items.push({
                text,
                x: xTopLeft,
                y: yTopLeft,
                width: Math.max(wNorm * renderedW, 8),
                height: Math.max(hNorm * renderedH, 8),
                source: 'gemini'
            });
        });

        logger.log(`[OCR] Gemini extracted ${items.length} items`);

        return {
            success: true,
            items,
            source: 'gemini'
        };
    } catch (error: any) {
        logger.error('[OCR] Gemini Vision extraction failed:', error);
        return {
            success: false,
            items: [],
            source: 'none',
            error: 'Gemini Vision extraction failed'
        };
    }
}

const ISO_WORD_REGEX = /\b(ISO\s*\d+(?:[-\s]*[A-Za-z]{1,4})?|DIN\s*\d+(?:[-\s]*[A-Za-z]{1,4})?|ASME\s*[A-Za-z]\d+(?:\.[0-9]+)?|EN\s*ISO\s*\d+(?:[-\s]*[A-Za-z]{1,4})?)/gi;

export interface IsoSentencesResult {
    success: boolean;
    sentences: string[];
    error?: string;
}

/**
 * Extract ISO/standard designations only from text fields (lines) that contain them.
 * Groups PDF text items by line (same Y), then for each line that contains ISO pattern,
 * extracts only the standard text from that line - not from other parts of the page.
 */
export async function extractIsoSentencesFromPdf(
    file: File,
    pageNumber: number
): Promise<IsoSentencesResult> {
    try {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await loadPdfDocument(arrayBuffer);
        const page = await pdf.getPage(pageNumber);
        const textContent = await page.getTextContent();
        const viewport = page.getViewport({ scale: 1 });

        const items: { str: string; y: number; x: number }[] = [];
        for (const item of textContent.items) {
            if (!('str' in item) || !item.str) continue;
            const ti = item as { str: string; transform: number[] };
            const ty = ti.transform[5];
            items.push({ str: ti.str, x: ti.transform[4], y: viewport.height - ty });
        }
        items.sort((a, b) => {
            const lineDiff = Math.round(a.y / 8) - Math.round(b.y / 8);
            return lineDiff !== 0 ? lineDiff : a.x - b.x;
        });

        const lineTolerance = 8;
        const lines: string[] = [];
        let currentLine = '';
        let lastY = -9999;
        for (const it of items) {
            if (Math.abs(it.y - lastY) > lineTolerance && currentLine) {
                lines.push(currentLine.trim());
                currentLine = '';
            }
            currentLine += (currentLine ? ' ' : '') + it.str;
            lastY = it.y;
        }
        if (currentLine) lines.push(currentLine.trim());

        const matching: string[] = [];
        const seen = new Set<string>();
        const re = new RegExp(ISO_WORD_REGEX.source, 'gi');

        for (const line of lines) {
            if (!line) continue;
            re.lastIndex = 0;
            let m: RegExpExecArray | null;
            while ((m = re.exec(line)) !== null) {
                const std = m[1].trim();
                if (std && !seen.has(std)) {
                    seen.add(std);
                    matching.push(std);
                }
            }
        }
        return { success: true, sentences: matching };
    } catch (error: any) {
        logger.error('[OCR] ISO sentences extraction failed:', error);
        return { success: false, sentences: [], error: error?.message };
    }
}

const ISO286_STANDARD_REGEX = /ISO\s*286(?:[-\s]*\d)?/i;

export interface Iso286DesignationResult {
    success: boolean;
    designation: string | null;
    error?: string;
}

/**
 * Extract ISO 286 designation (e.g. H7, h6, H7/h6) from PDF lines that contain ISO 286.
 * Used to apply standard tolerances to nominals without tolerances.
 */
export async function extractIso286DesignationFromPdf(
    file: File,
    pageNumber: number
): Promise<Iso286DesignationResult> {
    try {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await loadPdfDocument(arrayBuffer);
        const page = await pdf.getPage(pageNumber);
        const textContent = await page.getTextContent();
        const viewport = page.getViewport({ scale: 1 });

        const items: { str: string; y: number; x: number }[] = [];
        for (const item of textContent.items) {
            if (!('str' in item) || !item.str) continue;
            const ti = item as { str: string; transform: number[] };
            const ty = ti.transform[5];
            items.push({ str: ti.str, x: ti.transform[4], y: viewport.height - ty });
        }
        items.sort((a, b) => {
            const lineDiff = Math.round(a.y / 8) - Math.round(b.y / 8);
            return lineDiff !== 0 ? lineDiff : a.x - b.x;
        });

        const lineTolerance = 8;
        const lines: string[] = [];
        let currentLine = '';
        let lastY = -9999;
        for (const it of items) {
            if (Math.abs(it.y - lastY) > lineTolerance && currentLine) {
                lines.push(currentLine.trim());
                currentLine = '';
            }
            currentLine += (currentLine ? ' ' : '') + it.str;
            lastY = it.y;
        }
        if (currentLine) lines.push(currentLine.trim());

        for (const line of lines) {
            if (!line || !ISO286_STANDARD_REGEX.test(line)) continue;
            const designation = extractIso286Designation(line);
            if (designation) {
                return { success: true, designation };
            }
        }
        return { success: true, designation: null };
    } catch (error: any) {
        logger.error('[OCR] ISO 286 designation extraction failed:', error);
        return { success: false, designation: null, error: error?.message };
    }
}

export interface Iso22081TitleBlockExtractionResult {
    success: boolean;
    linear: string;
    shape: string;
    angular: string;
    radiusChamfer: string;
    mentions22081: boolean;
    error?: string;
}

const PDF_LINE_GROUP_TOL = 8;

function buildLinesFromPdfTextItems(
    items: { str: string; x: number; y: number }[],
    lineTolerance: number
): string[] {
    if (items.length === 0) return [];
    const sorted = [ ...items ].sort((a, b) => {
        const lineDiff = Math.round(a.y / lineTolerance) - Math.round(b.y / lineTolerance);
        return lineDiff !== 0 ? lineDiff : a.x - b.x;
    });
    const lines: string[] = [];
    let currentLine = '';
    let lastY = -9999;
    for (const it of sorted) {
        if (Math.abs(it.y - lastY) > lineTolerance && currentLine) {
            lines.push(currentLine.trim());
            currentLine = '';
        }
        currentLine += (currentLine ? ' ' : '') + it.str;
        lastY = it.y;
    }
    if (currentLine) lines.push(currentLine.trim());
    return lines;
}

/**
 * Collect PDF text from the title block (typical bottom / bottom-right), then full page if needed,
 * and parse ISO 22081 general tolerance bands (linear, shape, angular, radius & chamfer).
 */
export async function extractIso22081TitleBlockFromPdf(
    file: File,
    pageNumber: number
): Promise<Iso22081TitleBlockExtractionResult> {
    const emptyOk = (): Iso22081TitleBlockExtractionResult => ({
        success: true,
        linear: '',
        shape: '',
        angular: '',
        radiusChamfer: '',
        mentions22081: false
    });
    try {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await loadPdfDocument(arrayBuffer);
        const page = await pdf.getPage(pageNumber);
        const textContent = await page.getTextContent();
        const viewport = page.getViewport({ scale: 1 });
        const vw = viewport.width;
        const vh = viewport.height;

        const rawItems: { str: string; x: number; y: number }[] = [];
        for (const item of textContent.items) {
            if (!('str' in item) || !item.str) continue;
            const ti = item as { str: string; transform: number[]; width?: number };
            const ty = ti.transform[5];
            const y = viewport.height - ty;
            rawItems.push({ str: ti.str, x: ti.transform[4], y });
        }

        const inTitleBlock = (x: number, y: number) =>
            y > vh * 0.52 || (x > vw * 0.45 && y > vh * 0.4);

        const titleItems = rawItems.filter((it) => inTitleBlock(it.x, it.y));
        const titleLines = buildLinesFromPdfTextItems(titleItems, PDF_LINE_GROUP_TOL);
        const titleText = titleLines.join('\n');

        let parsed = parseIso22081ToleranceBandsFromTitleBlockText(titleText);
        if (!parsed || !parsed.mentions22081) {
            const allLines = buildLinesFromPdfTextItems(rawItems, PDF_LINE_GROUP_TOL);
            const fullText = allLines.join('\n');
            parsed = parseIso22081ToleranceBandsFromTitleBlockText(fullText);
        }

        if (!parsed) {
            return emptyOk();
        }

        return {
            success: true,
            linear: parsed.linear,
            shape: parsed.shape,
            angular: parsed.angular,
            radiusChamfer: parsed.radiusChamfer,
            mentions22081: parsed.mentions22081
        };
    } catch (error: any) {
        logger.error('[OCR] ISO 22081 title block extraction failed:', error);
        return {
            success: false,
            linear: '',
            shape: '',
            angular: '',
            radiusChamfer: '',
            mentions22081: false,
            error: error?.message
        };
    }
}

/**
 * Gemini Vision: read general tolerance table for ISO 22081 from a title-block image crop.
 */
export async function extractIso22081TolerancesViaGemini(
    canvas: HTMLCanvasElement,
    stageWidth: number,
    stageHeight: number,
    regionArg: RegionBounds
): Promise<Iso22081TitleBlockExtractionResult> {
    const emptyFail = (err?: string): Iso22081TitleBlockExtractionResult => ({
        success: false,
        linear: '',
        shape: '',
        angular: '',
        radiusChamfer: '',
        mentions22081: false,
        error: err
    });
    try {
        const region = normalizeRegionBounds(regionArg);
        const { cropX, cropY, cropW, cropH } = mapStageRegionToCanvasBitmapRect(
            canvas,
            region,
            stageWidth,
            stageHeight
        );
        const { base64: base64Data, mimeType } = encodeCanvasRegionForVisionApi(
            canvas,
            cropX,
            cropY,
            cropW,
            cropH
        );
        if (!base64Data) {
            return emptyFail('Failed to capture canvas region');
        }

        const headers = await getAuthHeaders();
        const apiUrl = buildApiUrl('ocr/extract');

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                image: base64Data,
                mimeType,
                prompt:
                    'This image is a fragment of a metric technical drawing (often the title block or a corner). ' +
                    'If you see reference to ISO 22081 (or "22081") and general tolerance values for categories, ' +
                    'return ONLY one JSON object, no markdown: ' +
                    '{"mentions22081":true|false,"linear":"","shape":"","angular":"","radiusChamfer":""}. ' +
                    'Fill each string with the single numeric tolerance value after ± for that category ' +
                    '(linear sizes, shape/form sizes, angular sizes, radius and chamfer / R and C), using dot as decimal separator. ' +
                    'Use empty string if unknown. If ISO 22081 is not referenced, set mentions22081 to false and leave values empty.'
            })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return emptyFail(
                typeof (errorData as { error?: string }).error === 'string'
                    ? (errorData as { error: string }).error
                    : `Server error: ${response.status}`
            );
        }

        const result = await response.json();
        if (result && result.success === false) {
            return emptyFail(typeof result.error === 'string' ? result.error : 'Recognition failed');
        }
        const text = typeof result.text === 'string' ? result.text.trim() : '';
        if (!text) {
            return {
                success: true,
                linear: '',
                shape: '',
                angular: '',
                radiusChamfer: '',
                mentions22081: false
            };
        }

        let obj: Record<string, unknown> | null = null;
        const cleaned = stripMarkdownCodeFences(text);
        try {
            obj = JSON.parse(cleaned) as Record<string, unknown>;
        } catch {
            const m = cleaned.match(/\{[\s\S]*\}/);
            if (m) {
                try {
                    obj = JSON.parse(m[0]) as Record<string, unknown>;
                } catch {
                    obj = null;
                }
            }
        }
        if (!obj) {
            return { success: true, linear: '', shape: '', angular: '', radiusChamfer: '', mentions22081: false };
        }

        const str = (k: string) => (typeof obj![k] === 'string' ? (obj![k] as string).trim() : '');
        const mentions = obj.mentions22081 === true;

        return {
            success: true,
            linear: str('linear'),
            shape: str('shape'),
            angular: str('angular'),
            radiusChamfer: str('radiusChamfer'),
            mentions22081: mentions
        };
    } catch (error: any) {
        logger.error('[OCR] ISO 22081 Gemini title block failed:', error);
        return emptyFail(error?.message);
    }
}

export { textReferencesIso22081 };
